/**
 * Copies a web (Stripe) subscription onto the gateway's `users` row.
 *
 * The web decides Pro from apps/api, but everything that runs on the gateway
 * (text limits, images, PDFs, voice, the tutor, email) enforces the tier on the
 * phone's `users` row, and a web-first account's row is minted Free
 * (server/gateway/webIdentity.ts). The only thing that copies an entitlement
 * onto it is `POST /api/v1/subscription/sync`, which pulls apps/api's
 * `/internal/native-entitlement` for the caller's stable id and applies it
 * (server/gateway/subscriptionAuthority.ts `syncServerEntitlement`). Only the
 * phone ever called it, so a web subscriber stayed Free everywhere the gateway
 * decides.
 *
 * apps/api finds the subscription by `linked_app_user_id`, so the stable id is
 * relayed first (gateway.ts `pushGatewaySid`); without that the sync finds
 * nothing and changes nothing.
 */

const SYNCED_FOR_KEY = 'baba.gateway.entitlement_synced'

type TierRank = 0 | 1 | 2

export function gatewayTierRank(tier: unknown): TierRank {
  return tier === 'pro_plus' ? 2 : tier === 'pro' ? 1 : 0
}

/**
 * True when apps/api says this account is paying and the gateway row is on a
 * lower tier. An unknown or failed apps/api answer is never a reason to sync.
 */
export function gatewayEntitlementLags(
  api: { status?: string; is_pro?: boolean; tier?: string; fetchFailed?: boolean; authExpired?: boolean } | null | undefined,
  me: { subscription_tier?: unknown; [key: string]: unknown } | null | undefined,
): boolean {
  if (!api || !me) return false
  if (api.fetchFailed || api.authExpired) return false
  const active = api.status === 'active' || api.is_pro === true
  if (!active) return false
  const paidFor: TierRank = api.tier === 'pro_plus' ? 2 : 1
  return gatewayTierRank(me.subscription_tier) < paidFor
}

/**
 * At most one reconciliation per stable id per tab session: it costs the
 * gateway a RevenueCat and an apps/api round trip, and if the first one did
 * not move the tier a second one will not either.
 */
export function claimEntitlementSyncForSession(stableUserId: string): boolean {
  try {
    if (typeof sessionStorage === 'undefined') return false
    if (sessionStorage.getItem(SYNCED_FOR_KEY) === stableUserId) return false
    sessionStorage.setItem(SYNCED_FOR_KEY, stableUserId)
    return true
  } catch {
    return false
  }
}

/**
 * Relay the stable id, ask the gateway to re-read the entitlement, then publish
 * the account's usage from the updated row so every meter shows the paid
 * allowance. Never throws; resolves false when any step failed.
 */
export async function syncGatewayEntitlement(opts: { stableUserId?: string } = {}): Promise<boolean> {
  try {
    const gateway = await import('./gateway')
    const stableUserId =
      opts.stableUserId ?? (await gateway.fetchGatewayMe().then((me) => (typeof me.stable_user_id === 'string' ? me.stable_user_id : undefined)))
    if (stableUserId) await gateway.pushGatewaySid(stableUserId)
    await gateway.gatewayFetchJson('/subscription/sync', { method: 'POST' })
    gateway.invalidateGatewayMe()
    const [me, { usageSnapshotFromGatewayMe }] = await Promise.all([
      gateway.fetchGatewayMe({ fresh: true }),
      import('./translateGateway/usageFromMe'),
    ])
    const usage = usageSnapshotFromGatewayMe(me as Parameters<typeof usageSnapshotFromGatewayMe>[0])
    if (usage) {
      try {
        localStorage.setItem('baba.app.last_usage', JSON.stringify(usage))
      } catch {}
      window.dispatchEvent(new CustomEvent('baba:usage-updated', { detail: usage }))
    }
    return true
  } catch {
    return false
  }
}
